import React, { useCallback } from 'react'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { Button as AtomButton, LinkButton } from '@/atoms/Button'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { ShareOutlineIcon } from '@/atoms/Icons/ShareOutlineIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { AsH1, LabelLG, ParagraphMD, TitleXS } from '@/atoms/Text'
import { HOMESTEAD_FILM_SERIES_EPISODE_1_ID, HOMESTEAD_SERIES_EPISODE_1_ID } from '@/constants/episode-ids'
import { paths } from '@/constants/paths'
import { projectTypes } from '@/constants/projectTypes'
import { GuildSubscribeButton } from '@/molecules/GuildSubscribeButton'
import { PayItForwardButton } from '@/molecules/PayItForwardButton'
import { GiftGuildButton } from '@/molecules/PayItForwardButton/GuildGiftButton'
import { CatalogTitle, findHydraExternalId, hasHydraIdAndSlug } from '@/services/ContentCatalog'
import { useGuildMember } from '@/services/GuildUserService'
import { Episode, ProjectType } from '@/services/ProjectsService'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { useAngelPlayerContext } from '../AngelPlayer'

interface CollectiblesPlayerInformationSectionProps {
  description: string
  handlePifButtonClick: () => void
  hasPayItForward: boolean
  name?: string
  projectSlug: string
  projectType?: ProjectType
  subtitle: string
  shouldShowGuildCTA?: boolean
  handleGuildButtonClick?: () => void
  episode: Episode
  setShowShareModal: (showShareModal: boolean) => void
  isSellingTickets: boolean
  watchableTitle?: CatalogTitle | null
  isGuildShareAvailable?: boolean
}

export const CollectiblesPlayerInformationSection: React.FC<CollectiblesPlayerInformationSectionProps> = React.memo(
  ({
    description,
    handlePifButtonClick,
    hasPayItForward,
    name,
    projectSlug,
    projectType,
    subtitle,
    shouldShowGuildCTA,
    handleGuildButtonClick,
    episode,
    setShowShareModal,
    isSellingTickets,
    watchableTitle,
    isGuildShareAvailable = false,
  }) => {
    const { t } = useTranslate('watch')
    const track = useSafeTrack()
    const { isGuildMember } = useGuildMember()

    const handleShareButtonClick = useCallback(() => {
      track('Share With Friend Button Clicked', { projectSlug, context: 'Player' })
      setShowShareModal(true)
    }, [track, projectSlug, setShowShareModal])

    const { playingProgress } = useAngelPlayerContext()
    const timeCreditsStarts = React.useMemo(() => {
      if (!episode?.source?.duration) {
        return 0
      }
      if (episode?.source?.credits) {
        return episode.source.credits
      } else {
        return episode?.source.duration - 60
      }
    }, [episode?.source?.credits, episode?.source?.duration])

    const canSeeShareButton =
      isGuildShareAvailable &&
      watchableTitle &&
      hasHydraIdAndSlug(watchableTitle) &&
      isGuildMember &&
      episode?.id &&
      findHydraExternalId(watchableTitle) === episode.id
    const isShowJoinGuildButton =
      !shouldShowGuildCTA && !isGuildMember && (isHomesteadEp1(episode) ? playingProgress > timeCreditsStarts : true)
    const isShowPifButton = hasPayItForward && !shouldShowGuildCTA && !isShowJoinGuildButton
    const isShowGiftGuildButton = isGuildMember

    return (
      <>
        <PaddedContainer fluid className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:pt-6">
          <div className="flex flex-col gap-2">
            <header className="mt-10 flex flex-col space-y-8 md:mt-4">
              <When condition={projectType !== projectTypes.movie}>
                <ParagraphMD aria-label="player-episode-mapping" color="core-gray-500" className="mb-0.5">
                  {name}
                </ParagraphMD>
              </When>
              <TitleXS weight="bold" as={AsH1} aria-label="player-episode-title" className="mb-1">
                {subtitle}
              </TitleXS>
            </header>

            <ParagraphMD className="hidden max-w-[600px] lg:block" color="core-gray-300">
              {description}
            </ParagraphMD>
          </div>

          <div className="flex flex-col flex-wrap gap-2 lg:flex-row-reverse">
            <LazyHydrate whenVisible>
              <When condition={canSeeShareButton}>
                <AtomButton
                  onClick={handleShareButtonClick}
                  variant="core-gray-800"
                  className="flex h-12 flex-row items-center justify-center gap-2 whitespace-nowrap rounded-[50px] px-[18px] py-[7px]"
                >
                  <ShareOutlineIcon size={24} className="-mt-0.5" />
                  <LabelLG className="mt-[1px] flex h-full items-center justify-center">
                    {t('shareFreeAccess', 'Share Free Access')}
                  </LabelLG>
                </AtomButton>
              </When>
            </LazyHydrate>

            <When condition={isSellingTickets}>
              <LinkButton
                href={`/tickets/${episode.projectSlug}`}
                className="h-12 gap-2 whitespace-nowrap"
                variant="white"
                outline
              >
                <TicketIcon color="white" size={24} />
                {t('getShowtimes', 'Get Showtimes')}
              </LinkButton>
            </When>

            <When condition={shouldShowGuildCTA}>
              <GuildSubscribeButton
                className="max-h-12 w-full whitespace-nowrap"
                projectSlug={projectSlug}
                projectType={projectType}
                onClick={handleGuildButtonClick}
              />
            </When>

            <When condition={isShowJoinGuildButton}>
              <LinkButton
                href={`${paths.guild.join}/${projectSlug}`}
                variant={'oxide'}
                className={'max-h-12 w-full whitespace-nowrap px-10 py-3 lg:w-fit'}
              >
                <div className="relative flex flex-row items-center justify-center gap-2">
                  <GuildIconMono color="white" size={20} className="mr-1" />
                  {t('joinTheGuild', 'Join The Guild')}
                </div>
              </LinkButton>
            </When>

            <When condition={isShowGiftGuildButton && !isSellingTickets}>
              <GiftGuildButton className="h-12" />
            </When>

            <When condition={!isShowGiftGuildButton && isShowPifButton}>
              <PayItForwardButton
                className="max-h-12 w-full whitespace-nowrap md:w-full lg:w-fit"
                pifEnabled={hasPayItForward}
                projectSlug={projectSlug}
                onClick={handlePifButtonClick}
                utmSource="below-video-player-pif-button"
              />
            </When>
          </div>

          <ParagraphMD className="lg:hidden" color="core-gray-300">
            {description}
          </ParagraphMD>
        </PaddedContainer>
      </>
    )
  },
)

CollectiblesPlayerInformationSection.displayName = 'React.memo(CollectiblesPlayerInformationSection)'

function isHomesteadEp1(episode: Episode) {
  return episode.id === HOMESTEAD_SERIES_EPISODE_1_ID || episode.id === HOMESTEAD_FILM_SERIES_EPISODE_1_ID
}
